import styled from "@emotion/styled"
import Accordion from "@mui/material/Accordion"
import AccordionDetails from "@mui/material/AccordionDetails"
import AccordionSummary from "@mui/material/AccordionSummary"
import { styled as miuiStyled } from "@mui/material/styles"
import { DonationMessages } from "app/[locale]/(layout)/donation-application-form/page"
import { useEffect } from "react"
import { CheckIcon, Container, NewText, useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import { extractProperty } from "utils"
import StepA from "./StepA"
import StepB from "./StepB"
import StepC from "./StepC"

const StyledContainer = styled.div`
  margin: 8px 0;
  ${(props) => props.theme.breakpoints.up("md")} {
    margin: 20px 0;
  }
  .MuiAccordionSummary-expandIconWrapper {
    transform: none !important;
  }
`

const StyledAccordionDetails = miuiStyled(AccordionDetails)(() => ({
  padding: 10,
}))

type Props = {
  data: any
  control: any
  index: number
  isStepAValid: any
  isStepBValid: any
  isStepCValid: any
  activeAcordion: any
  setAtiveAcordion: any
  errors: any
  watch: any
  formFiles: any
  setFormFiles: any
  handleSubmit: any
  trigger: any
  errorOnFiles: boolean
  messages: DonationMessages
}

const FormStep: React.FC<Props> = ({
  data,
  control,
  index,
  isStepAValid,
  isStepBValid,
  setAtiveAcordion,
  activeAcordion,
  errors,
  watch,
  formFiles,
  setFormFiles,
  isStepCValid,
  handleSubmit,
  trigger,
  errorOnFiles,
  messages,
}) => {
  const title = extractProperty(data, "stepTitle")

  const stepToRender = () => {
    switch (index) {
      case 3:
        return (
          <StepA
            control={control}
            errors={errors}
            watch={watch}
            setAtiveAcordion={setAtiveAcordion}
            trigger={trigger}
            messages={messages}
          />
        )
      case 4:
        return (
          <StepB
            messages={messages}
            control={control}
            errors={errors}
            watch={watch}
            setAtiveAcordion={setAtiveAcordion}
            trigger={trigger}
          />
        )
      case 5:
        return (
          <StepC
            messages={messages}
            control={control}
            errors={errors}
            watch={watch}
            setAtiveAcordion={setAtiveAcordion}
            isStepAValid={isStepAValid}
            isStepBValid={isStepBValid}
            formFiles={formFiles}
            setFormFiles={setFormFiles}
            isStepCValid={isStepCValid}
            handleSubmit={handleSubmit}
            errorOnFiles={errorOnFiles}
          />
        )
    }
  }

  const isDisabled = () => {
    if (!watch("IsDonationPolicy") || watch("IsDonationPolicy") === "false") {
      return true
    }
    if (index === 3) {
      return false
    }
    if (index === 4) {
      return !isStepAValid()
    }
    if (index === 5) {
      return !isStepAValid() || !isStepBValid()
    }
  }

  const showCheckIcon = () => {
    if (index === 3) {
      return !!isStepAValid()
    }
    if (index === 4) {
      return !!isStepAValid() && !!isStepBValid()
    }
    if (index === 5) {
      return !!isStepAValid() && !!isStepBValid() && !!isStepCValid()
    }
  }

  const isDonationPolicy = watch("IsDonationPolicy")

  useEffect(() => {
    if (!isDonationPolicy || isDonationPolicy === "false") {
      setAtiveAcordion(0)
    } else {
      setAtiveAcordion(3)
    }
  }, [isDonationPolicy, setAtiveAcordion])

  const isDesktop = useIsDesktop()
  return (
    <Container>
      <StyledContainer>
        <Accordion
          style={{
            padding: 0,
            margin: 0,
            marginBlockStart: isDesktop ? 40 : 16,
            boxShadow: "0px 4px 12px 0px rgba(0, 0, 0, 0.08)",
            border: "none",
            borderRadius: 8,
            opacity: 1,
          }}
          disabled={isDisabled()}
          expanded={activeAcordion === index ? true : false}
          sx={{
            "&:before": {
              display: "none",
            },
          }}
          onChange={() => {
            setAtiveAcordion(index)
          }}
        >
          <AccordionSummary
            aria-controls="panel1a-content"
            id="panel1a-header"
            style={{
              border: "none",
              outline: "none",
              color: isDisabled() ? "#6F6F6F" : "white",
              backgroundColor: isDisabled() ? "#F7F7F7" : sharedColors.propBlue,
              borderRadius: 8,
              borderBottomLeftRadius: activeAcordion === index ? 0 : 8,
              borderBottomRightRadius: activeAcordion === index ? 0 : 8,
              height: "56px",
            }}
            expandIcon={showCheckIcon() ? <CheckIcon /> : null}
          >
            <NewText
              desktopFontSize={18}
              desktopFontWeight="600"
              desktopLineHeight="26px"
              mobileFontSize={16}
              mobileFontWeight="600"
              mobileLineHeight={"23px"}
              component="p"
              maxWidth={"80%"}
              color={isDisabled() ? "#6F6F6F" : "white"}
            >
              {title}
            </NewText>
          </AccordionSummary>
          <StyledAccordionDetails
            sx={{
              backgroundColor: "#F7F7F7",
              padding: isDesktop ? "24px" : "16px",
            }}
          >
            {stepToRender()}
          </StyledAccordionDetails>
        </Accordion>
      </StyledContainer>
    </Container>
  )
}
export default FormStep
